<script>
    import tinyPumperIncidentsService from '@/services/tinyPumperIncidents.service';

    const OWNER_CLASSES = Object.freeze({
        Engineering: 'text-bg-danger',
        Field: 'text-bg-warning',
        Ops: 'text-bg-primary',
        Vendor: 'text-bg-secondary'
    });

    /**
     * Open incidents grouped by cause, each group carrying its playbook.
     *
     * All the copy comes from the server (TinyPumperCausePlaybook). Nothing about a cause is
     * written twice, and a cause added to the engine shows up here with its own words rather than
     * as a blank card.
     */
    export default {
        name: 'TinyPumperIncidentTriage',
        emits: ['filter-cause'],
        data() {
            return {
                // null, not [] — "we have not asked yet" and "nothing is open" are opposite
                // answers on a health screen and must not render the same.
                groups: null,
                isLoading: false,
                hasFailed: false
            };
        },
        computed: {
            getGroups() {
                return this.groups || [];
            },
            isEmpty() {
                return !!this.groups && this.groups.length === 0;
            }
        },
        methods: {
            ownerClass(owner) {
                return OWNER_CLASSES[owner] || 'text-bg-light';
            },
            // A group whose every incident is snoozed or acknowledged is somebody's problem already.
            isHandled(group) {
                return group.openCount > 0 && group.handledCount >= group.openCount;
            },
            ageDays(openedAt) {
                if (!openedAt) return 0;

                return Math.max(0, Math.floor((Date.now() - new Date(openedAt)) / (1000 * 60 * 60 * 24)));
            },
            loadTriage() {
                this.isLoading = true;
                this.hasFailed = false;

                return tinyPumperIncidentsService
                    .triage()
                    .then(groups => {
                        this.groups = groups ?? [];
                    })
                    // Left unknown rather than shown as an empty list: "nobody has anything to do"
                    // is the one answer this panel must never give by accident.
                    .catch(() => {
                        this.groups = null;
                        this.hasFailed = true;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            },
            handleShowIncidents(group) {
                this.$emit('filter-cause', group.playbook.cause);
            }
        },
        mounted() {
            this.loadTriage();
        }
    };
</script>

<template>
    <div class="tp-incident-triage">
        <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
            <button type="button" class="btn btn-outline-secondary text-nowrap" :disabled="isLoading" @click="loadTriage">
                <i class="bi bi-arrow-clockwise me-1"></i>
                Refresh
            </button>
            <small class="text-muted">
                Grouped by cause, most actionable first. Snoozed and acknowledged incidents are counted here — they are
                handled, not gone.
            </small>
            <span v-if="isLoading" class="spinner-border spinner-border-sm text-muted" role="status"></span>
        </div>

        <div v-if="hasFailed" class="alert alert-warning d-flex align-items-center gap-2 py-2" role="alert">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>Could not load the triage groups. This is not an empty fleet — reload to try again.</span>
        </div>

        <div v-else-if="isEmpty" class="card card-body text-center py-4 bg-light">
            No open incidents. Every active sensor is reporting ✅
        </div>

        <div v-else class="row g-3">
            <div v-for="group in getGroups" :key="`tp-triage-${group.playbook.cause}`" class="col-12 col-xxl-6">
                <div class="card h-100 tp-incident-triage__group" :class="{ 'tp-incident-triage__group--handled': isHandled(group) }">
                    <div class="card-body">
                        <div class="d-flex flex-wrap align-items-center gap-2">
                            <h5 class="card-title mb-0 me-auto">{{ group.playbook.title }}</h5>
                            <span class="badge" :class="ownerClass(group.playbook.owner)">
                                <i class="bi bi-person-fill me-1"></i>
                                {{ group.playbook.owner }}
                            </span>
                            <span v-if="isHandled(group)" class="badge text-bg-success">
                                <i class="bi bi-check2-circle me-1"></i>
                                Handled
                            </span>
                        </div>

                        <div class="d-flex flex-wrap align-items-baseline gap-3 mt-2">
                            <span>
                                <span class="fs-4 fw-bold">{{ group.openCount }}</span>
                                <small class="text-muted ms-1">open</small>
                            </span>
                            <span>
                                <span class="fw-bold">{{ group.affectedSensors }}</span>
                                <small class="text-muted ms-1">sensors</small>
                            </span>
                            <span :title="group.oldestOpenedAt ? new Date(group.oldestOpenedAt).toLocaleString() : ''">
                                <span class="fw-bold">{{ ageDays(group.oldestOpenedAt) }}</span>
                                <small class="text-muted ms-1">days since the oldest opened</small>
                            </span>
                            <small class="text-muted">{{ group.snoozedCount }} snoozed · {{ group.acknowledgedCount }} acknowledged</small>
                        </div>

                        <p class="mt-3 mb-2">{{ group.playbook.whatItMeans }}</p>

                        <div class="alert alert-light border d-flex align-items-baseline gap-2 mb-0">
                            <i class="bi bi-signpost-2-fill"></i>
                            <span>{{ group.playbook.suggestedAction }}</span>
                        </div>
                    </div>
                    <div class="card-footer bg-transparent">
                        <button type="button" class="btn btn-sm btn-outline-secondary" @click="handleShowIncidents(group)">
                            <i class="bi bi-list-ul me-1"></i>
                            Show these incidents
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .tp-incident-triage {
        &__group {
            &--handled {
                opacity: 0.75;
                border-color: var(--bs-success-border-subtle);
            }
        }
    }
</style>
